import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { Animated } from 'react-native';

const NavigationVisibilityContext = createContext(null);

export const NavigationVisibilityProvider = ({ children }) => {
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [isBottomNavVisible, setIsBottomNavVisible] = useState(true);
  const headerAnimation = useRef(new Animated.Value(0)).current;
  const bottomNavAnimation = useRef(new Animated.Value(0)).current;
  const lastScrollY = useRef(0);
  const scrollThreshold = 10; // Minimum scroll distance to trigger hide/show
  
  const showHeader = useCallback(() => {
    if (!isHeaderVisible) {
      setIsHeaderVisible(true);
      Animated.timing(headerAnimation, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }).start();
    }
  }, [isHeaderVisible, headerAnimation]);

  const hideHeader = useCallback(() => {
    if (isHeaderVisible) {
      setIsHeaderVisible(false);
      Animated.timing(headerAnimation, {
        toValue: -100, // Move header up
        duration: 200,
        useNativeDriver: true,
      }).start();
    }
  }, [isHeaderVisible, headerAnimation]);

  const showBottomNav = useCallback(() => {
    if (!isBottomNavVisible) {
      setIsBottomNavVisible(true);
      Animated.timing(bottomNavAnimation, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }).start();
    }
  }, [isBottomNavVisible, bottomNavAnimation]);

  const hideBottomNav = useCallback(() => {
    if (isBottomNavVisible) {
      setIsBottomNavVisible(false);
      Animated.timing(bottomNavAnimation, {
        toValue: 100, // Move bottom nav down
        duration: 200,
        useNativeDriver: true,
      }).start();
    }
  }, [isBottomNavVisible, bottomNavAnimation]);

  const handleScroll = useCallback((event) => {
    // Auto-hide functionality disabled - header and bottom nav always visible
    // const currentScrollY = event.nativeEvent.contentOffset.y;
    // const diff = currentScrollY - lastScrollY.current;
    
    // Keep navigation elements always visible
    showHeader();
    showBottomNav();
  }, [showHeader, showBottomNav]);

  const resetVisibility = useCallback(() => {
    lastScrollY.current = 0;
    showHeader();
    showBottomNav();
  }, [showHeader, showBottomNav]);

  return (
    <NavigationVisibilityContext.Provider
      value={{
        isHeaderVisible,
        isBottomNavVisible,
        headerAnimation,
        bottomNavAnimation,
        handleScroll,
        showHeader,
        hideHeader,
        showBottomNav,
        hideBottomNav,
        resetVisibility,
      }}
    >
      {children}
    </NavigationVisibilityContext.Provider>
  );
};

export const useNavigationVisibility = () => {
  const context = useContext(NavigationVisibilityContext);
  if (!context) {
    throw new Error('useNavigationVisibility must be used within a NavigationVisibilityProvider');
  }
  return context;
};
